import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Loader2, GraduationCap, Users, BarChart3, DollarSign, TrendingUp, Award } from "lucide-react";
import { fmtMoney } from "./money";


/**
 * LearnAnalyticsAdmin — Dashboard métriques LMS pour le praticien.
 * Route : /admin/learn-analytics
 */
export default function LearnAnalyticsAdmin() {
  const tr = useT("admin");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/learn/analytics")
      .then(({ data }) => setData(data))
      .catch(() => setData({ totals: {}, by_course: [] }));
  }, []);

  if (!data) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>;
  const t = data.totals;

  return (
    <div data-testid="learn-analytics" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-sol-gold" /> {tr("lan.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">{tr("lan.subtitle")}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={GraduationCap} label={tr("lan.courses")} value={t.courses_count || 0} suffix={tr("lan.published", { count: t.published_courses || 0 })} />
        <StatCard icon={Users} label={tr("lan.students")} value={t.total_students || 0} />
        <StatCard icon={TrendingUp} label={tr("lan.enrollments")} value={t.total_enrollments || 0} />
        <StatCard icon={DollarSign} label={tr("lan.revenue")} value={fmtMoney(t.total_revenue_cents || 0)} accent />
        <StatCard icon={Award} label={tr("lan.paidCourses")} value={data.by_course.filter((c) => c.price_cents > 0).length} />
      </div>

      <div className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden">
        <div className="p-5 border-b border-eh-border-soft">
          <h3 className="font-display text-xl">{tr("lan.perCourse")}</h3>
        </div>
        {data.by_course.length === 0 ? (
          <p className="text-center text-eh-muted py-12">{tr("lan.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-eh-cream/40">
                <tr className="text-left text-xs uppercase tracking-wider text-eh-muted">
                  <th className="px-5 py-3">{tr("lan.colCourse")}</th>
                  <th className="px-3 py-3">{tr("lan.colStatus")}</th>
                  <th className="px-3 py-3 text-right">{tr("lan.colStudents")}</th>
                  <th className="px-3 py-3 text-right">{tr("lan.colCompleted")}</th>
                  <th className="px-3 py-3 text-right">{tr("lan.colProgress")}</th>
                  <th className="px-3 py-3 text-right">{tr("lan.colQuiz")}</th>
                  <th className="px-5 py-3 text-right">{tr("lan.colRevenue")}</th>
                </tr>
              </thead>
              <tbody>
                {data.by_course.map((c) => {
                  const completionRate = c.students_count > 0 ? Math.round(100 * c.completed_count / c.students_count) : 0;
                  return (
                    <tr key={c.course_id} className="border-t border-eh-border-soft" data-testid={`row-${c.course_id}`}>
                      <td className="px-5 py-3 font-medium text-eh-ink">{c.title}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${c.published ? "bg-emerald-100 text-emerald-700" : "bg-eh-cream text-eh-muted"}`}>
                          {c.published ? tr("lan.statusPublished") : tr("lan.statusDraft")}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">{c.students_count}</td>
                      <td className="px-3 py-3 text-right">
                        <span>{c.completed_count}</span>
                        {c.students_count > 0 && <span className="text-eh-muted text-xs ml-1">({completionRate}%)</span>}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-1.5 bg-eh-cream rounded-full overflow-hidden">
                            <div className="h-full bg-sol-gold" style={{ width: `${c.avg_progress_pct}%` }} />
                          </div>
                          <span className="text-eh-muted text-xs w-9 text-right">{c.avg_progress_pct}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right text-eh-muted">{c.avg_quiz_score != null ? `${c.avg_quiz_score}%` : "—"}</td>
                      <td className="px-5 py-3 text-right font-medium">{c.revenue_cents > 0 ? fmtMoney(c.revenue_cents, c.currency) : <span className="text-eh-muted">—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, suffix, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-sol-gold/10 border-sol-gold/30" : "bg-white border-eh-border-soft"}`}>
      <div className="text-xs uppercase tracking-wider text-eh-muted mb-2 inline-flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-display text-2xl text-eh-ink">{value}</div>
      {suffix && <div className="text-xs text-eh-muted mt-0.5">{suffix}</div>}
    </div>
  );
}
